'use client';

import { FeedbackQuestionType } from '@up2stay/up2promo-api-client';
import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  cn,
  previewPanelSurfaceClass,
} from '@up2stay/up2promo-ui';
import { ArrowDown, ArrowUp, Eye, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { UpgradeCard } from '@/core/components/billing/upgrade-surfaces';
import { FeedbackFeatureGate } from '@/core/components/feedback/feedback-feature-gate';
import { FeedbackPageBreadcrumb } from '@/core/components/feedback/feedback-page-breadcrumb';
import { FeedbackPhonePreview } from '@/core/components/feedback/feedback-phone-preview';
import { ProfileAccentColorField } from '@/core/components/profile/profile-accent-color-field';
import {
  FEEDBACK_SECTION_CARD_CLASS,
  FEEDBACK_SECTION_CARD_CONTENT_CLASS,
  FEEDBACK_SECTION_TITLE_CLASS,
} from '@/core/components/feedback/feedback-surface';
import { PageHeader } from '@/core/components/page-header';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  useCreateFeedbackFormTemplate,
  useDeleteFeedbackFormTemplate,
  useFeedbackFormTemplate,
  useUpdateFeedbackFormTemplate,
  useFeedbackFeatureEnabled,
} from '@/core/hooks/use-feedback';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { moveListItem } from '@/core/lib/move-list-item';
import {
  workspaceBrandAccent,
  workspaceBrandLogoUrl,
} from '@/core/lib/workspace-brand';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { AppIonPage } from '@/core/components/app-ion-page';

type QuestionDraft = {
  key: string;
  type: FeedbackQuestionType;
  label: string;
  required: boolean;
  optionsText: string;
};

const QUESTION_TYPES = [
  FeedbackQuestionType.SHORT_TEXT,
  FeedbackQuestionType.LONG_TEXT,
  FeedbackQuestionType.YES_NO,
  FeedbackQuestionType.NPS_0_10,
  FeedbackQuestionType.RATING_1_5,
  FeedbackQuestionType.SINGLE_SELECT,
  FeedbackQuestionType.MULTI_SELECT,
] as const;

function newQuestion(): QuestionDraft {
  return {
    key: crypto.randomUUID(),
    type: FeedbackQuestionType.SHORT_TEXT,
    label: '',
    required: false,
    optionsText: '',
  };
}

export function FeedbackFormEditorPage() {
  const dictRoot = useDictionary();
  const dict = dictRoot.feedback;
  const isMobile = useIsMobile();
  const { navigateShell } = useNavigateShell();
  const params = useParams<{ templateId?: string }>();
  const isNew = !params.templateId || params.templateId === 'new';
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const feedbackEnabled = useFeedbackFeatureEnabled();
  const { isFeatureEnabled, isLoading: entitlementsLoading } =
    useEntitlements();
  const canHideBranding = isFeatureEnabled('feature.feedback');

  const { data: existing } = useFeedbackFormTemplate(
    workspaceId,
    isNew ? undefined : params.templateId,
  );
  const createTemplate = useCreateFeedbackFormTemplate(workspaceId);
  const updateTemplate = useUpdateFeedbackFormTemplate(workspaceId);
  const deleteTemplate = useDeleteFeedbackFormTemplate(workspaceId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [title, setTitle] = useState('');
  const [showUp2promoBranding, setShowUp2promoBranding] = useState(true);
  const [accentColor, setAccentColor] = useState<string | null>(null);
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [hydratedId, setHydratedId] = useState<string | null>(
    isNew ? '__new__' : null,
  );
  if (!isNew && existing && hydratedId !== existing.id) {
    setHydratedId(existing.id);
    setName(existing.name);
    setDescription(existing.description ?? '');
    setTitle(existing.title);
    setShowUp2promoBranding(existing.showUp2promoBranding !== false);
    setAccentColor(existing.accentColor ?? null);
    setQuestions(
      existing.questions.map((q) => ({
        key: q.id,
        type: q.type as FeedbackQuestionType,
        label: q.label,
        required: q.required,
        optionsText: (q.options ?? []).join('\n'),
      })),
    );
  }

  const previewQuestions = questions
    .filter((q) => q.label.trim())
    .map((q) => ({
      id: q.key,
      type: q.type,
      label: q.label.trim(),
      required: q.required,
      options: q.optionsText
        .split('\n')
        .map((o) => o.trim())
        .filter(Boolean),
    }));

  const previewLabels = {
    overallStars: dict.preview.overallStars,
    comment: dict.preview.comment,
    commentPlaceholder: dict.preview.commentPlaceholder,
    required: dict.preview.required,
    yes: dict.preview.yes,
    no: dict.preview.no,
    scaleLow: dict.preview.scaleLow,
    scaleHigh: dict.preview.scaleHigh,
    selectPlaceholder: dict.preview.selectPlaceholder,
    submit: dict.preview.submit,
    previewNote: dict.preview.previewNote,
  };

  const effectiveShowUp2promoBranding = canHideBranding
    ? showUp2promoBranding
    : true;

  const preview = (
    <FeedbackPhonePreview
      title={title || dict.forms.editor.titlePlaceholder}
      workspaceName={workspace?.name}
      logoUrl={workspaceBrandLogoUrl(workspace?.photo)}
      accentColor={workspaceBrandAccent(accentColor)}
      questions={previewQuestions}
      labels={previewLabels}
      showUp2promoBranding={effectiveShowUp2promoBranding}
    />
  );

  const onSave = async () => {
    if (!name.trim() || !title.trim()) {
      toast.error(dict.forms.editor.needNameTitle);
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        title: title.trim(),
        showUp2promoBranding: effectiveShowUp2promoBranding,
        accentColor,
        questions: previewQuestions.map((q) => ({
          id: q.id,
          type: q.type,
          label: q.label,
          required: q.required,
          options:
            q.type === FeedbackQuestionType.SINGLE_SELECT ||
            q.type === FeedbackQuestionType.MULTI_SELECT
              ? q.options
              : undefined,
        })),
      };
      if (isNew) {
        const created = await createTemplate.mutateAsync(body);
        toast.success(dict.forms.editor.saveSuccess);
        if (created?.id) {
          navigateShell(shellPaths.feedbackFormDetail(created.id));
        }
      } else {
        await updateTemplate.mutateAsync({
          templateId: params.templateId as string,
          body,
        });
        toast.success(dict.forms.editor.saveSuccess);
      }
    } catch {
      toast.error(dict.forms.editor.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (isNew || !params.templateId) return;
    try {
      await deleteTemplate.mutateAsync(params.templateId);
      toast.success(dict.forms.editor.deleteSuccess);
      navigateShell(shellPaths.feedbackLibrary);
    } catch {
      toast.error(dict.forms.editor.deleteFailed);
    }
  };

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout>
          <PageHeader
            title={
              isNew
                ? dict.forms.editor.createTitle
                : dict.forms.editor.editTitle
            }
            breadcrumb={
              <FeedbackPageBreadcrumb
                domainLabel={dictRoot.nav.navDomainWork}
                pageLabel={dictRoot.nav.feedbackLibrary}
              />
            }
            backHref={shellPaths.feedbackLibrary}
            actions={
              // Locked plan: nothing to save or preview (DEV-549).
              feedbackEnabled && (
                <div className="flex items-center gap-2">
                  {isMobile ? (
                    <Button
                      type="button"
                      variant="ghostAccent"
                      size="sm"
                      onClick={() => setPreviewOpen(true)}
                    >
                      <Eye className="size-4" />
                      {dict.compose.preview}
                    </Button>
                  ) : null}
                  {!isNew ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => void onDelete()}
                      aria-label={dict.forms.editor.deleteForm}
                      title={dict.forms.editor.deleteForm}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    disabled={saving}
                    onClick={() => void onSave()}
                  >
                    {dict.forms.editor.save}
                  </Button>
                </div>
              )
            }
          />
          <FeedbackFeatureGate>
            <div className="grid gap-6 md:grid-cols-[minmax(0,1.2fr)_minmax(260px,0.85fr)]">
              <Card className={cn('min-w-0', FEEDBACK_SECTION_CARD_CLASS)}>
                <CardContent
                  className={cn(
                    'space-y-5',
                    FEEDBACK_SECTION_CARD_CONTENT_CLASS,
                    'pt-5 sm:pt-6',
                  )}
                >
                  <div className="space-y-2">
                    <Label htmlFor="feedback-form-name">
                      {dict.forms.editor.name}
                    </Label>
                    <Input
                      id="feedback-form-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={dict.forms.editor.namePlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="feedback-form-description">
                      {dict.forms.editor.descriptionLabel}
                    </Label>
                    <Input
                      id="feedback-form-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={dict.forms.editor.descriptionPlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-0.5">
                      <Label htmlFor="feedback-form-guest-title">
                        {dict.forms.editor.guestTitle}
                      </Label>
                      <SettingsInfoHint label={dict.forms.editor.guestTitle}>
                        {dict.forms.editor.guestTitleHelp}
                      </SettingsInfoHint>
                    </div>
                    <Input
                      id="feedback-form-guest-title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={dict.forms.editor.titlePlaceholder}
                    />
                  </div>

                  <div className="space-y-3 rounded-[var(--r-control)] border border-border/60 bg-card p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-0.5">
                        <Label htmlFor="showUp2promoBranding">
                          {dict.forms.editor.showUp2promoBranding}
                        </Label>
                        <SettingsInfoHint
                          label={dict.forms.editor.showUp2promoBranding}
                        >
                          {dict.forms.editor.showUp2promoBrandingDescription}
                        </SettingsInfoHint>
                      </div>
                      <Switch
                        id="showUp2promoBranding"
                        checked={effectiveShowUp2promoBranding}
                        disabled={entitlementsLoading || !canHideBranding}
                        onCheckedChange={setShowUp2promoBranding}
                      />
                    </div>
                    {!entitlementsLoading && !canHideBranding ? (
                      <UpgradeCard
                        feature="feature.feedback"
                        compact
                        title={
                          dict.forms.editor.showUp2promoBrandingUpgradeTitle
                        }
                        description={
                          dict.forms.editor
                            .showUp2promoBrandingUpgradeDescription
                        }
                      />
                    ) : null}
                    <ProfileAccentColorField
                      value={accentColor}
                      labels={{
                        label: dict.forms.editor.accentColor || 'Accent color',
                        description:
                          dict.forms.editor.accentColorDescription ||
                          'Used on the guest feedback form.',
                        reset: dict.forms.editor.accentColorReset || 'Default',
                        placeholder:
                          dict.forms.editor.accentColorPlaceholder || '#2A7FFF',
                      }}
                      onChange={setAccentColor}
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-0.5">
                        <h2 className={FEEDBACK_SECTION_TITLE_CLASS}>
                          {dict.forms.editor.questions}
                        </h2>
                        <SettingsInfoHint label={dict.forms.editor.questions}>
                          {dict.forms.editor.questionsHelp}
                        </SettingsInfoHint>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="dashed"
                        disabled={questions.length >= 5}
                        onClick={() =>
                          setQuestions((prev) => [...prev, newQuestion()])
                        }
                      >
                        <Plus className="size-4" />
                        {dict.forms.editor.addQuestion}
                      </Button>
                    </div>
                    {questions.map((q, index) => (
                      <div
                        key={q.key}
                        className="space-y-3 rounded-[var(--r-control)] border border-border/60 bg-card p-4"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold tracking-tight">
                            {dict.forms.editor.questionN.replace(
                              '{n}',
                              String(index + 1),
                            )}
                          </p>
                          {/* Reorder with buttons, keyboard-friendly on
                              every device (DEV-769). */}
                          <div className="flex items-center gap-0.5">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="size-8"
                              disabled={index === 0}
                              aria-label={dict.forms.editor.moveUp.replace(
                                '{n}',
                                String(index + 1),
                              )}
                              onClick={() =>
                                setQuestions((prev) =>
                                  moveListItem(prev, index, -1),
                                )
                              }
                            >
                              <ArrowUp className="size-4" />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="size-8"
                              disabled={index === questions.length - 1}
                              aria-label={dict.forms.editor.moveDown.replace(
                                '{n}',
                                String(index + 1),
                              )}
                              onClick={() =>
                                setQuestions((prev) =>
                                  moveListItem(prev, index, 1),
                                )
                              }
                            >
                              <ArrowDown className="size-4" />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="size-8"
                              aria-label={dict.forms.editor.removeQuestion.replace(
                                '{n}',
                                String(index + 1),
                              )}
                              onClick={() =>
                                setQuestions((prev) =>
                                  prev.filter((item) => item.key !== q.key),
                                )
                              }
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`q-label-${q.key}`}>
                            {dict.forms.editor.questionLabel}
                          </Label>
                          <Input
                            id={`q-label-${q.key}`}
                            value={q.label}
                            onChange={(e) =>
                              setQuestions((prev) =>
                                prev.map((item) =>
                                  item.key === q.key
                                    ? { ...item, label: e.target.value }
                                    : item,
                                ),
                              )
                            }
                          />
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label id={`q-type-${q.key}`}>
                              {dict.forms.editor.questionType}
                            </Label>
                            <Select
                              value={q.type}
                              onValueChange={(v) =>
                                setQuestions((prev) =>
                                  prev.map((item) =>
                                    item.key === q.key
                                      ? {
                                          ...item,
                                          type: v as FeedbackQuestionType,
                                        }
                                      : item,
                                  ),
                                )
                              }
                            >
                              <SelectTrigger
                                aria-labelledby={`q-type-${q.key}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {QUESTION_TYPES.map((type) => (
                                  <SelectItem key={type} value={type}>
                                    {dict.questionTypes[type]}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-end justify-between gap-3 pb-1">
                            <Label htmlFor={`req-${q.key}`}>
                              {dict.forms.editor.required}
                            </Label>
                            <Switch
                              id={`req-${q.key}`}
                              checked={q.required}
                              onCheckedChange={(checked) =>
                                setQuestions((prev) =>
                                  prev.map((item) =>
                                    item.key === q.key
                                      ? { ...item, required: checked }
                                      : item,
                                  ),
                                )
                              }
                            />
                          </div>
                        </div>
                        {q.type === FeedbackQuestionType.SINGLE_SELECT ||
                        q.type === FeedbackQuestionType.MULTI_SELECT ? (
                          <div className="space-y-2">
                            <Label htmlFor={`q-options-${q.key}`}>
                              {dict.forms.editor.options}
                            </Label>
                            <Textarea
                              id={`q-options-${q.key}`}
                              value={q.optionsText}
                              onChange={(e) =>
                                setQuestions((prev) =>
                                  prev.map((item) =>
                                    item.key === q.key
                                      ? { ...item, optionsText: e.target.value }
                                      : item,
                                  ),
                                )
                              }
                              placeholder={dict.forms.editor.optionsPlaceholder}
                            />
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <aside className="hidden md:flex md:justify-center">
                <div
                  className={cn(
                    'rounded-[var(--r-card)] border p-4 shadow-[var(--shadow-card)]',
                    previewPanelSurfaceClass,
                  )}
                >
                  {preview}
                </div>
              </aside>
            </div>
          </FeedbackFeatureGate>
        </AppPageLayout>
      </AppPageContent>

      <ResponsiveDialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <ResponsiveDialogContent scrollable={false}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              {dict.compose.preview}
            </ResponsiveDialogTitle>
          </ResponsiveDialogHeader>
          <div className="flex justify-center px-1 pb-4">{preview}</div>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </AppIonPage>
  );
}
